import { useCallback, useState } from 'react';
import { Percent } from 'lucide-react';
import { reconReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, Dash, errMessage } from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { TaxGlReconciliationDto, TaxGlRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'TAX_GL_RECON';

const STATUS_TONES: Record<string, PillTone> = {
  MATCHED:        'green',
  DIFFERENCE:     'red',
  NOT_CONFIGURED: 'grey',
  INFO_ONLY:      'blue',
};

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code',     header: 'Tax Code',        width: 14 },
  { key: 'Name',     header: 'Tax Name',        width: 26 },
  { key: 'Side',     header: 'Side',            width: 10 },
  { key: 'Scope',    header: 'Scope',           width: 16 },
  { key: 'Register', header: 'Register Amount', width: 16, numFmt: '#,##0.00' },
  { key: 'Gl',       header: 'GL Movement',     width: 16, numFmt: '#,##0.00' },
  { key: 'Unposted', header: 'Unposted Tax',    width: 16, numFmt: '#,##0.00' },
  { key: 'Diff',     header: 'Difference',      width: 16, numFmt: '#,##0.00' },
  { key: 'Status',   header: 'Status',          width: 16 },
  { key: 'Accounts', header: 'Mapped Accounts', width: 24 },
  { key: 'Notes',    header: 'Notes',           width: 40 },
];

function buildExportRows(rows: TaxGlRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Code:     r.taxCode,
    Name:     r.taxName,
    Side:     r.side,
    Scope:    r.scope,
    Register: r.registerAmount,
    Gl:       r.glMovement ?? '',
    Unposted: r.unpostedTaxAmount,
    Diff:     r.difference ?? '',
    Status:   r.status,
    Accounts: r.mappedAccountCodes ?? '',
    Notes:    r.notes ?? '',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const TaxGlReconPage = () => {
  const { hasPermission } = usePermission();

  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState<TaxGlReconciliationDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reconReportApi.getTaxGl({ fromDate, toDate });
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  if (!hasPermission(Permissions.RPT_RECON)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const rows = data?.rows ?? [];

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Tax vs GL',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(rows),
      title: 'Tax Register vs GL Reconciliation',
      subtitle: `${data.companyName} · ${data.fromDate} to ${data.toDate}`,
    }, `TaxGlRecon_${data.fromDate}_${data.toDate}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(rows), `TaxGlRecon_${data.fromDate}_${data.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Percent size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Tax Register vs GL</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={rows.length}
          filterSummary={`${fromDate}..${toDate}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <label className={LABEL_CLS}>From Date *</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
            </div>
            <div className="w-44">
              <label className={LABEL_CLS}>To Date *</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
            </div>
          </div>
          <RunReportButton onClick={() => { void run(); }} isLoading={loading} disabled={!fromDate || !toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to compare the tax register against GL movements.' />
      )}

      {data && (
        <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tax Register vs GL — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)} · Generated {fmtDate(data.generatedAt)}
              </p>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{rows.length} rows</span>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm border-collapse min-w-[1300px]">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                <tr>
                  <th className={cn(TH_CLS, 'w-24')}>Tax Code</th>
                  <th className={TH_CLS}>Tax Name</th>
                  <th className={cn(TH_CLS, 'w-20')}>Side</th>
                  <th className={cn(TH_CLS, 'w-36')}>Scope</th>
                  <th className={TH_R_CLS}>Register</th>
                  <th className={TH_R_CLS}>GL Movement</th>
                  <th className={TH_R_CLS}>Unposted Tax</th>
                  <th className={TH_R_CLS}>Difference</th>
                  <th className={cn(TH_CLS, 'w-32')}>Status</th>
                  <th className={TH_CLS}>Mapped Accounts</th>
                  <th className={TH_CLS}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                      No tax activity in the selected window.
                    </td>
                  </tr>
                ) : rows.map((r, i) => {
                  const diffBad = r.difference !== null && Math.abs(r.difference) >= 0.01;
                  return (
                    <tr key={`${r.taxCode}-${r.side}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100">{r.taxCode}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{r.taxName}</td>
                      <td className="px-3 py-2">
                        <Pill label={r.side} tone={r.side === 'INPUT' ? 'blue' : 'purple'} />
                      </td>
                      <td className="px-3 py-2">
                        {r.scope === 'COMPANY_TOTAL'
                          ? <Pill label="COMPANY TOTAL" tone="amber" title="Role-account fallback — reconciled at company level, not per tax code" />
                          : <Pill label="TAX CODE" tone="grey" />}
                      </td>
                      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.registerAmount)}</td>
                      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                        {r.glMovement === null ? <Dash /> : fmtAmtZero(r.glMovement)}
                      </td>
                      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.unpostedTaxAmount)}</td>
                      <td className={cn(
                        AMT_CLS,
                        diffBad ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                      )}>
                        {r.difference === null ? <Dash /> : fmtAmtZero(r.difference)}
                      </td>
                      <td className="px-3 py-2">
                        <Pill label={r.status} tone={STATUS_TONES[r.status] ?? 'grey'} />
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.mappedAccountCodes ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.notes ?? <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
